import React, { useEffect, useState } from 'react';
import { Field, change, formValueSelector, reduxForm } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { compose } from 'redux';

// Internal Components
import CommonFormComponent from '../../../CommonField/CommonFormComponent';
import TravelReason from '../../DropDownContents/TravelReason';
import CopyTextBlock from '../../CopyTextBlock/CopyTextBlock';
import Loader from '../../../Loader/Loader';


// Helpers / Actions
import messages from '../../../../locale/jsonMessages';
import submit from './submit';
import { closeAIModal } from '../../../../actions/modalActions';
import { AI_BUTTON_LOADER } from '../../../../constants';

// Images
import { AIIcon } from '../../../Common/Assets/SvgIconComponent/CommonSvgIcon';

import cs from '../../../../components/commonStyle.css';
import s from '../../AIFeatureStyle.css';


function ReservationMessage({ handleSubmit, isContactHost, handleCloseModal, formValues }) {

    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const selector = formValueSelector("ReservationMessage");

    // Form Values
    const tripStart = useSelector(state => selector(state, 'checkIn'));
    const tripEnd = useSelector(state => selector(state, 'checkOut'));

    // AI States
    const aiResponseContent = useSelector(state => state?.aiContents?.reservationMessage);
    const aiDataLoader = useSelector(state => state?.aiContents?.aiResponseLoading);


    const [isCopied, setIsCopied] = useState(false);
    const [showGenerate, setShowGenerate] = useState(true);
    const [messagesCache, setMessagesCache] = useState([]);

    useEffect(() => {
        if (aiResponseContent && Object.keys(aiResponseContent).length > 0) {
            setMessagesCache(aiResponseContent);
            setShowGenerate(false);
            dispatch(change('ReservationMessage', 'aiMessageContent', aiResponseContent?.message));
        }

    }, [aiResponseContent]);

    useEffect(() => {
        if (messagesCache && messagesCache.message !== '') {
            dispatch(change('ReservationMessage', 'aiMessageContent', messagesCache?.message))
        }
    }, [messagesCache]);


    const handleApply = () => {
        if (isContactHost) {
            dispatch(change('ContactHostForm', 'travelReason', formValues?.travelReason));
            handleCloseModal && handleCloseModal();
            dispatch(change('ContactHostForm', 'content', aiResponseContent?.message));
            dispatch(closeAIModal());
        } else {
            dispatch(change('PaymentForm', 'message', aiResponseContent?.message));
            dispatch(closeAIModal());
        }
    };

    const handleCancel = () => {  // cancel action
        dispatch(closeAIModal())
        if (isContactHost) { handleCloseModal && handleCloseModal() }
    }

    const handleGenerate = async (values) => {
        dispatch(change('ContactHostForm', 'travelReason', formValues?.travelReason));
        await handleSubmit(submit)(values);
        setShowGenerate(false);
    };

    const handleCopy = () => {
        if (!aiResponseContent?.message) return;

        navigator.clipboard.writeText(aiResponseContent?.message);
        setIsCopied(true);

        setTimeout(() => setIsCopied(false), 1000);
    };

    return (
        <form>
            <div className='oneColumnGrid'>
                <div className={cx(cs.displayFlex, cs.flexWrap, cs.gap12)}>
                    <div className={s.formValueChip}>
                        <span className={cs.fontWeightMedium}>{formatMessage(messages.tripStartDate)}:</span>&nbsp;
                        <span className='secondaryText'>{tripStart}</span>
                    </div>

                    <div className={s.formValueChip}>
                        <span className={cs.fontWeightMedium}>{formatMessage(messages.tripEndDate)}:</span>&nbsp;
                        <span className='secondaryText'>{tripEnd}</span>
                    </div>
                </div>
                <TravelReason />
                <Field
                    name="aiMessage"
                    component={CommonFormComponent}
                    labelText={formatMessage(messages.aiTextAreaLabel)}
                    placeholder={formatMessage(messages.aiTextAreaPlaceholder)}
                    componentClass="textarea"
                    inputClass='commonTextAreaField'
                />

                {!showGenerate &&
                    <div className={s.aiGenerateInputWrapper}>
                        <div className={s.aiGenerateBoxLine}>
                            <CopyTextBlock
                                isCopied={isCopied}
                                onCopy={handleCopy}
                                label={formatMessage(messages.contactMessage)}
                                copiedLabel={formatMessage(messages.copiedLabel)}
                                content={aiResponseContent?.message}
                                containerClass={s.aiGenerateTextBox}
                            />
                        </div>
                    </div>
                }
            </div>

            <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween, cs.gap12, cs.flexWrap, cs.spaceTop4, cs.mobileAlignFlexStretch, cs.mobileFlexColumn)}>
                <div className={cx(cs.displayFlex, cs.gap12, cs.justifyContentSpaceBetween, cs.alignCenter, cs.flexWrap, cs.flexGrow)}>
                    {!showGenerate &&
                        <Loader
                            type="button"
                            handleClick={handleGenerate}
                            className={cs.regenerateBtn}
                            label={<>
                                <AIIcon />
                                <span className={cs.aiTextColor}>{formatMessage(messages.reGenerateLabel)}</span>
                            </>}
                            show={aiDataLoader}
                            spinnerColor={AI_BUTTON_LOADER}
                        />
                    }

                    <div className={cx('doubleBtnFlexEnd', cs.flexGrow)}>
                        <Button onClick={() => handleCancel()} className={cs.btnPrimaryBorder}>
                            <FormattedMessage {...messages.deSelect} />
                        </Button>

                        {aiDataLoader && (messagesCache && Object.keys(messagesCache).length == 0) ?
                            <Loader
                                type="button"
                                handleClick={handleGenerate}
                                label={formatMessage(messages.generateLabel)}
                                show={aiDataLoader}
                                className={cx(cs.btnPrimary, 'loaderButtonFlex')}
                            />
                            :
                            showGenerate &&
                            <Button type="button" onClick={handleGenerate} className={cs.btnPrimary}>
                                <FormattedMessage {...messages.generateLabel} />
                            </Button>
                        }
                    </div>
                </div>

                {!showGenerate &&
                    <Button onClick={handleApply} className={cs.btnPrimary}>
                        <FormattedMessage {...messages.apply} />
                    </Button>
                }
            </div>


        </form>
    );
}

export default compose(
    withStyles(cs, s),
    reduxForm({
        form: 'ReservationMessage'
    }))(ReservationMessage);
